/**
 * Quality persistence (PR Q2 · §Q2.2).
 *
 * Local adapters for lint mutes + refactor votes. Pre-prod stash is
 * localStorage; the API stays stable for a future Firestore swap.
 *
 * Defensive: missing localStorage (SSR / locked-down env) falls back
 * to an in-memory object so consumers never throw.
 */
import type { Mute } from './lintLibrary.js';

const MUTES_KEY = 'atlas.quality.mutes';
const VOTES_KEY = 'atlas.quality.votes';

export type VoteDirection = 'up' | 'down';

/** R1.M4 — write outcomes are explicit so UI can show "couldn't save"
 *  feedback instead of silently dropping the user's edit on a quota
 *  bust. `reason: 'quota'` is the actionable branch (clear stale data
 *  / sign in to sync); `'unknown'` is the catch-all. */
export type PersistResult =
  | { ok: true }
  | { ok: false; reason: 'quota' | 'unknown'; message?: string };

interface KV { [k: string]: unknown }

let memoryStore: Record<string, string> = {};

function getStore(): { get: (k: string) => string | null; set: (k: string, v: string) => void } {
  if (typeof localStorage !== 'undefined') {
    return {
      get: (k) => localStorage.getItem(k),
      set: (k, v) => localStorage.setItem(k, v),
    };
  }
  return {
    get: (k) => memoryStore[k] ?? null,
    set: (k, v) => { memoryStore[k] = v; },
  };
}

function readJson<T extends KV>(key: string): T {
  try {
    const raw = getStore().get(key);
    if (!raw) return {} as T;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {} as T;
    return parsed as T;
  } catch {
    return {} as T;
  }
}

export function isQuotaError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  // QuotaExceededError on most browsers; NS_ERROR_DOM_QUOTA_REACHED on Firefox legacy.
  return e.name === 'QuotaExceededError'
    || e.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || /quota/i.test(e.message);
}

export function writeJson(key: string, value: KV): PersistResult {
  try {
    getStore().set(key, JSON.stringify(value));
    return { ok: true };
  } catch (e) {
    if (isQuotaError(e)) {
      return { ok: false, reason: 'quota', message: (e as Error).message };
    }
    return { ok: false, reason: 'unknown', message: e instanceof Error ? e.message : String(e) };
  }
}

// ── Mutes ──────────────────────────────────────────────────────────────

function isValidMute(x: unknown): x is Mute {
  if (typeof x !== 'object' || x === null) return false;
  const m = x as Mute;
  return typeof m.mutedUntil === 'number'
    && typeof m.mutedBy === 'string'
    && typeof m.reason === 'string';
}

export function readMutes(): Record<string, Mute> {
  // R1.M1 — shape-validate every value; drop bad entries silently so a
  // poisoned localStorage entry can't surface `undefined.mutedUntil`.
  const raw = readJson<Record<string, unknown>>(MUTES_KEY);
  const out: Record<string, Mute> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (isValidMute(v)) out[k] = v;
  }
  return out;
}

export function writeMute(key: string, mute: Mute): PersistResult {
  const all = readMutes();
  all[key] = mute;
  return writeJson(MUTES_KEY, all);
}

export function clearMute(key: string): PersistResult {
  const all = readMutes();
  delete all[key];
  return writeJson(MUTES_KEY, all);
}

// ── Votes ──────────────────────────────────────────────────────────────

function isValidVote(x: unknown): x is VoteDirection {
  return x === 'up' || x === 'down';
}

export function readVotes(): Record<string, VoteDirection> {
  // R1.M1 — drop entries whose value isn't 'up' | 'down'.
  const raw = readJson<Record<string, unknown>>(VOTES_KEY);
  const out: Record<string, VoteDirection> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (isValidVote(v)) out[k] = v;
  }
  return out;
}

export function writeVote(proposalId: string, direction: VoteDirection): PersistResult {
  const all = readVotes();
  all[proposalId] = direction;
  return writeJson(VOTES_KEY, all);
}

export function clearVote(proposalId: string): PersistResult {
  const all = readVotes();
  delete all[proposalId];
  return writeJson(VOTES_KEY, all);
}
